import React, { useContext, useState } from 'react';
import { Icon, Card } from '@bb/elements-web';
import styled from 'styled-components';
import getLiveConciergePrice from 'api/SupraOwnerContractDefaultPricingController/getLiveConciergePrice';
import { formatCurrency } from 'utils';
import { AuthContext } from 'contexts';
import AnsweringServiceContext from '../../AnsweringServiceContext';

/**
 * The interactive card for the user to get a price estimate of subscribing
 * to Live Concierge Service.
 */
const PriceEstimatorCard: React.FC = () => {
  const { viewingLevel } = useContext(AnsweringServiceContext);
  const { auth } = useContext(AuthContext);
  const [listingCount, setListingCount] = useState<number>(100);
  const [unitPrice, setUnitPrice] = useState<number>();

  /** Function for saving the entered number only if it valid */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // Allow empty string for user to clear and retype
    if (value === '') {
      setListingCount(1);
      return;
    }

    const numValue = parseInt(value, 10);

    // Only accept positive integers between 1 and 1000
    // eslint-disable-next-line no-restricted-globals
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 1000) {
      setListingCount(numValue);
    }
  };

  /** Function for grabbing the LCS price based on the entered number */
  const recalculatePrice = async () => {
    const individualUnitPrice: number = await getLiveConciergePrice({
      calculationType: viewingLevel,
      unitCount: listingCount,
      country: auth.org.country,
    });
    setUnitPrice(individualUnitPrice);
  };

  return (
    <StyledCard
      title="Price Estimator"
      $withContent={false}
      dataCy="price-estimator-card"
    >
      <CardContent>
        <Label>
          Number of Active {viewingLevel === 'org' ? 'Agents' : 'Listings'}
        </Label>
        <StyledInput
          type="number"
          min={1}
          max={1000}
          value={listingCount}
          onChange={handleInputChange}
        />
        <button onClick={recalculatePrice}>
          <Icon icon="arrowRightOutlined" size={15} />{' '}
        </button>
        {/* To discuss during full implementation: I would really like to use the <RightCircleOutlined /> icon here from Antd to trigger the price calculation, but the mockups have no icon. The reason I am adding one is because now we are calculating the price via a Mongo call instead of locally after pulling in *all* the prices via a single Mongo call like we were before. So it's a more expensive operation. */}
        <AutoSelectText>
          Select my current total # of{' '}
          {viewingLevel === 'org' ? 'agents' : 'listings'}
        </AutoSelectText>
        <PriceBox>
          <PriceLabel>Your monthly price</PriceLabel>
          <PriceRow>
            <PriceAmount>{formatCurrency(unitPrice)}</PriceAmount>
            <PricePeriod>/ month</PricePeriod>
          </PriceRow>
        </PriceBox>
      </CardContent>
    </StyledCard>
  );
};

export default PriceEstimatorCard;

export const StyledCard = styled(Card)<{ $withContent?: boolean }>`
  width: 313px;

  .ant-card-head-title {
    font-weight: 700;
  }

  .ant-card-head {
    border-bottom: 0px;
  }

  .ant-card-body {
    padding: 0px 24px 24px 24px;
  }

  ${({ $withContent }) =>
    $withContent &&
    `.ant-card-body {
      padding: 1px 0px;
    }`}
`;

const CardContent = styled.div`
  padding: 0;
`;

const Label = styled.label`
  display: block;
  font-weight: 400;
  margin-bottom: 8px;
  color: #585e72;
  font-size: 16px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const AutoSelectText = styled.p`
  color: #2c6ef2;
  margin: 12px 0;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const PriceBox = styled.div`
  border: 1px solid #a8ceff;
  background-color: #f0f7ff;
  border-radius: 6px;
  padding: 16px;
`;

const PriceLabel = styled.div`
  font-weight: 500;
  margin-bottom: 8px;
  font-size: 16px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

const PriceAmount = styled.span`
  font-size: 28px;
  font-weight: 700;
`;

const PricePeriod = styled.span`
  font-weight: 400;
  margin-bottom: 8px;
  color: #585e72;
  font-size: 16px;
`;
